'use strict';

/**
 * Plan Reconciliation (EAT-3922)
 *
 * A plan document is frozen at the moment it was generated: its items carry the
 * participants, scaling and logged amounts that were true for the household it was
 * built for. When that household changes — a family member joins or leaves, a portion
 * is adjusted — those numbers go stale, and scheduling the plan cooks for a family
 * that no longer exists.
 *
 * The server already solves this for the auto-populate path
 * (users/src/Chewba/Action/UGC/PopulateMeals.php::initializeMeals). This module is the
 * client-side equivalent, so a plan opened in the editor or scheduled from it produces
 * the same meals the server would have.
 *
 * What is recomputed (derived from the household):
 *   - participants
 *   - scaling
 *   - leftover_servings
 *   - logged_amount (the primary user's serving)
 *
 * What is never touched (authored by whoever built the plan):
 *   - which recipe/food, which day (offset), which meal slot
 *   - the fresh/leftover structure and how many leftover days there are
 *
 * The result is a derived copy. The plan document itself is never modified — the same
 * plan may be recommended to several patients, and each one reconciles it to their own
 * household independently.
 */

import indexBy from 'lodash.indexby';
import {
    getParticipantsForProfileByMealType,
    getParticipantsInfo,
    normalizeParticipantsToArray,
    recalculateScaling,
} from './Meals';

/**
 * Profile types that represent a real person whose household we can reconcile against.
 *
 * Set in Editor.react.js `getProfileFromProps`. Profiles that are absent from this list
 * are either a throwaway persona (`alternate-patient`, from the Try It Out flow) or an
 * untyped object — a favourite's stored snapshot, or the empty profile a PRO gets while
 * browsing favourites without a patient. None of those describe a household to cook for.
 *
 * @type {string[]}
 */
const RECONCILABLE_PROFILE_TYPES = ['patient', 'user'];

/**
 * Whether a plan should be reconciled against this profile.
 *
 * @param {Object} profile - Profile from the editor
 * @returns {boolean} True when the profile describes a real household
 */
export function canReconcile(profile) {
    return !!profile && RECONCILABLE_PROFILE_TYPES.includes(profile.type);
}

/**
 * True for participant ids that identify an ad-hoc guest rather than a household member.
 * Guests are added per-meal via Edit Meal Servings and never appear in the profile.
 *
 * @param {string} uuid - Participant id
 * @returns {boolean} True when this id represents a guest
 */
function isGuestId(uuid) {
    return !uuid || uuid === '' || uuid.startsWith('guest-');
}

/**
 * Resolve the participants for one plan item.
 *
 * An item that names participants is making a complete statement about who eats it —
 * whether that came from the generator, a dietitian editing servings, or the patient
 * unticking someone on a single meal. Anyone left off was left off deliberately, so the
 * profile is not consulted to put them back. The only thing reconciled is existence: a
 * participant who is no longer in the household is dropped, which is the divergence this
 * fix is for.
 *
 * An item that names nobody makes no statement — an older plan from before participants
 * were tracked — so the profile supplies the set for that meal slot.
 *
 * @param {Object} item - Plan item
 * @param {Object} slotParticipants - Participants by meal slot name, resolved from the profile
 * @param {Object} householdIndex - Household members (incl. USER) indexed by uuid
 * @returns {Array<string>} Participant ids
 */
function resolveParticipants(item, slotParticipants, householdIndex) {
    const recorded = normalizeParticipantsToArray(item.participants);

    if (recorded.length) {
        // Guests are per-meal additions that never appear in a profile, so they always stay.
        const kept = recorded.filter((uuid) => isGuestId(uuid) || householdIndex[uuid]);

        return kept.length ? kept : ['USER'];
    }

    const fromProfile = (slotParticipants[item.meal] || []).map((participant) => participant.uuid);

    return fromProfile.length ? fromProfile : ['USER'];
}

/**
 * Turn participant ids into the objects the scaling maths needs.
 *
 * @param {Array<string>} uuids - Participant ids
 * @param {Object} profile - Profile, for the primary user's portion
 * @param {Object} householdIndex - Household members indexed by uuid
 * @returns {Array<Object>} Participant objects carrying portions
 */
function toParticipantObjects(uuids, profile, householdIndex) {
    return uuids.map(
        (uuid) => householdIndex[uuid] || { uuid, name: 'Guest', portion: 1 }
    );
}

/**
 * Reconcile a plan's participants, scaling and logged amounts against a profile.
 *
 * Idempotent: when nothing has diverged the returned items are equivalent to the ones
 * passed in, and `changed` is empty.
 *
 * @param {Object} plan - Plan with an items array
 * @param {Object} profile - Profile with preferences.meal_types, family and portion
 * @param {Object} recipes - Recipes indexed by uuid
 * @param {Object} foods - Foods indexed by uuid
 * @returns {{items: Array<Object>, changed: Array<Object>}} Reconciled items, plus a
 *          description of every item whose participants or scaling moved
 */
export function reconcilePlanToProfile(plan, profile, recipes = {}, foods = {}) {
    if (!plan || !Array.isArray(plan.items) || !canReconcile(profile)) {
        return { items: plan?.items || [], changed: [] };
    }

    // Resolve each meal slot's participants once. getParticipantsForProfileByMealType
    // falls back to the primary user for a slot it cannot find, so only keep slots the
    // profile actually declares — the rest are handled per-item by resolveParticipants.
    const declaredSlots = (profile.preferences?.meal_types || []).map((mealType) => mealType.name);
    const slotParticipants = {};
    declaredSlots.forEach((name) => {
        slotParticipants[name] = getParticipantsForProfileByMealType(profile, name);
    });

    const householdIndex = indexBy(getParticipantsInfo(profile), 'uuid');
    const contentFor = (item) => recipes[item.recipe_uuid] || foods[item.food_uuid] || null;

    const items = plan.items.map((item) => ({ ...item }));
    const leftoversByParent = {};

    items.forEach((item) => {
        // Participants are resolved per item, leftovers included: a leftover can sit in a
        // different meal slot than the meal it came from, and may deliberately keep the
        // people who were eating it.
        item.participants = resolveParticipants(item, slotParticipants, householdIndex);

        // Only serving-logged items track the primary user's portion. An item logged in
        // its own unit (half a scoop, 200g) carries a deliberate per-dish amount, and
        // getLoggedServingsOfRecipe resolves those from logged_grams/logged_milliliters
        // anyway — rewriting logged_amount there would discard the amount and change
        // nothing about the scaling.
        if (item.logged_unit === 'serving') {
            item.logged_amount = profile.portion;
        }

        if (item.meal_type === 'leftover' && item.parent) {
            leftoversByParent[item.parent] = leftoversByParent[item.parent] || [];
            leftoversByParent[item.parent].push(item);
        }
    });

    // Participants were settled in the pass above, so read them back rather than resolving twice.
    const participantsFor = (item) => toParticipantObjects(item.participants, profile, householdIndex);

    items.forEach((item) => {
        if (item.meal_type === 'leftover') {
            return; // scaling is inherited from the parent below
        }

        const content = contentFor(item);

        if (!content) {
            return;
        }

        // Each leftover contributes the need of *its own* slot, not the parent's. The
        // server gets this wrong today (it adds the parent's need for every leftover),
        // which over-scales whenever a leftover lands in a slot with fewer people.
        const leftovers = (leftoversByParent[item.id] || []).map((leftover) => ({
            uuid: leftover.uuid || leftover.id,
            leftoverOffset: leftover.offset,
            mealType: leftover.meal,
            participants: participantsFor(leftover),
        }));

        item.scaling = recalculateScaling(item, participantsFor(item), content, leftovers, items, profile);

        (leftoversByParent[item.id] || []).forEach((leftover) => {
            leftover.scaling = item.scaling;
            leftover.leftover_servings = participantsFor(leftover).reduce(
                (total, participant) => total + (participant.portion || 0),
                0
            );
        });
    });

    const before = indexBy(plan.items, 'id');
    const changed = items
        .filter((item) => {
            const original = before[item.id];

            if (!original) {
                return false;
            }

            const participantsMoved =
                normalizeParticipantsToArray(original.participants).join(',') !== item.participants.join(',');

            return participantsMoved || (original.scaling || 1) !== item.scaling;
        })
        .map((item) => ({
            id: item.id,
            meal: item.meal,
            offset: item.offset,
            from: {
                participants: normalizeParticipantsToArray(before[item.id].participants),
                scaling: before[item.id].scaling,
            },
            to: { participants: item.participants, scaling: item.scaling },
        }));

    return { items, changed };
}

export default reconcilePlanToProfile;
